import { useAuth } from "@/shared/hooks/useAuth";
import { queryKeys } from "@/shared/lib/queryClient";
import AppsService from "@/shared/services/AppsService";
import { IApp } from "@/shared/types/apps";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useMemo } from "react";

// Получение списка всех приложений (публичный доступ)
export const useApps = (searchQuery?: string) => {
  const query = useQuery({
    queryKey: queryKeys.apps.lists(),
    queryFn: () => AppsService.getApps(),
    enabled: true, // Запрос доступен для всех пользователей
    staleTime: 5 * 60 * 1000, // Данные актуальны 5 минут
    gcTime: 10 * 60 * 1000, // Кэш храним 10 минут
    retry: (failureCount, error) => {
      // Не повторяем при ошибках 404
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Фильтрация приложений по поисковому запросу
  const filteredData = useMemo(() => {
    if (!query.data || !searchQuery?.trim()) {
      return query.data;
    }

    const searchTerm = searchQuery.toLowerCase().trim();
    return query.data.filter(
      (app: IApp) =>
        app.title.toLowerCase().includes(searchTerm) ||
        app.short_description.toLowerCase().includes(searchTerm)
    );
  }, [query.data, searchQuery]);

  return {
    ...query,
    data: filteredData,
  };
};

// Получение конкретного приложения по Slug
export const useApp = (slug: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.apps.detail(slug),
    queryFn: () => AppsService.getAppBySlug(slug),
    enabled: !!slug && isAuthenticated, // Запрос только если slug передан и пользователь авторизован
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // Не повторяем при ошибках авторизации и 404
      const axiosError = error as AxiosError;
      if (
        axiosError?.response?.status === 401 ||
        axiosError?.response?.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

// Хук для принудительного обновления кэша приложений
export const useRefreshApps = () => {
  const queryClient = useQueryClient();

  return {
    refreshApps: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.lists(),
      });
    },
    refreshApp: (id: string) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.apps.detail(id),
      });
    },
  };
};

// Получение конкретного приложения по Slug для публичного доступа
export const usePublicApp = (slug: string) => {
  return useQuery({
    queryKey: queryKeys.apps.detail(slug),
    queryFn: () => AppsService.getAppBySlug(slug),
    enabled: !!slug, // Запрос только если slug передан, без проверки авторизации
    staleTime: 5 * 60 * 1000, // Данные актуальны 5 минут
    gcTime: 10 * 60 * 1000, // Кэш храним 10 минут
    retry: (failureCount, error) => {
      // Не повторяем при ошибках 404
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
